import React from 'react';
import { Sun, Moon, RefreshCw, Download, Settings, AlertCircle, Clock, FileText, ChevronDown, Filter, X, ShieldCheck } from 'lucide-react';
import { Role, ViewType, ModalType } from '../data';
import { MultiSelect } from './MultiSelect';
interface HeaderProps {
  role: Role;
  setRole: (r: Role) => void;
  isDark: boolean;
  toggleDark: () => void;
  lastUpdated: string;
  onReset: () => void;
  onOpenAdmin: () => void;
  onRefresh: () => void;
  onOpenExport: () => void;
}
export function Header({
  role,
  setRole,
  isDark,
  toggleDark,
  lastUpdated,
  onReset,
  onOpenAdmin,
  onRefresh,
  onOpenExport
}: HeaderProps) {
  const roles: Role[] = ['Processor', 'Processor Assistant', 'Loan Owner', 'Team Lead', 'Admin'];
  return <header className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3 flex items-center justify-between" data-id="element-49">
      <div className="flex items-center space-x-6" data-id="element-50">
        <div className="flex items-center space-x-3" data-id="element-51">
          <div className="w-9 h-9 bg-cobalt-600 rounded-lg flex items-center justify-center shadow-sm" data-id="element-52">
            <span className="text-white font-bold text-base leading-none tracking-tighter" data-id="element-53">
              AL
            </span>
          </div>
          <div data-id="element-54">
            <h1 className="text-base font-semibold text-slate-900 dark:text-white leading-tight" data-id="element-55">
              Pipeline Console
            </h1>
            <div className="flex items-center text-xs text-slate-500 dark:text-slate-400 mt-0.5" data-id="element-56">
              <span className="relative flex h-2 w-2 mr-1.5" data-id="element-57">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" data-id="element-58"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" data-id="element-59"></span>
              </span>
              <span data-id="element-60">Updated {lastUpdated}</span>
              <button onClick={onRefresh} aria-label="Refresh" className="ml-1.5 p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-cobalt-600 dark:hover:text-cobalt-400 transition-colors" data-id="element-61">
                <RefreshCw className="w-3 h-3" data-id="element-62" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-2" data-id="element-63">
        <div className="relative" data-id="element-64">
          <select value={role} onChange={e => setRole(e.target.value as Role)} className="appearance-none bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm rounded-md pl-3 pr-8 py-1.5 focus:outline-none focus:ring-2 focus:ring-cobalt-500 dark:text-slate-200 cursor-pointer" data-id="element-65">
            {roles.map(r => <option key={r} value={r} data-id="element-66">
                {r} View
              </option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" data-id="element-67" />
        </div>

        <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 mx-1" data-id="element-68"></div>

        <button onClick={onReset} className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-cobalt-600 dark:hover:text-cobalt-400 px-3 py-1.5 rounded transition-colors" data-id="element-69">
          Reset View
        </button>

        {role === 'Admin' && <button onClick={onOpenExport} className="flex items-center space-x-1.5 text-sm font-medium bg-cobalt-600 hover:bg-cobalt-700 text-white px-3 py-1.5 rounded-md transition-colors shadow-sm" data-id="element-70">
            <Download className="w-4 h-4" data-id="element-71" />
            <span data-id="element-72">Export</span>
            <span className="inline-flex items-center text-[9px] uppercase font-bold bg-cobalt-800/60 px-1 rounded ml-0.5" data-id="element-73">
              <ShieldCheck className="w-2.5 h-2.5 mr-0.5" data-id="element-74" />
              Admin
            </span>
          </button>}

        <button onClick={toggleDark} aria-label="Toggle dark mode" className="p-1.5 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-id="element-75">
          {isDark ? <Sun className="w-5 h-5" data-id="element-76" /> : <Moon className="w-5 h-5" data-id="element-77" />}
        </button>

        {role === 'Admin' && <button onClick={onOpenAdmin} aria-label="Open settings" className="p-1.5 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-id="element-78">
            <Settings className="w-5 h-5" data-id="element-79" />
          </button>}
      </div>
    </header>;
}
interface ViewControlsProps {
  view: ViewType;
  setView: (v: ViewType) => void;
  role: Role;
}
export function ViewControls({
  view,
  setView,
  role
}: ViewControlsProps) {
  // Admin only sees a single "All In Process" pipeline — no toggle.
  if (role === 'Admin') {
    return <div className="inline-flex items-center px-3 py-1.5 bg-cobalt-50 dark:bg-cobalt-900/30 border border-cobalt-200 dark:border-cobalt-800 rounded-lg text-sm font-medium text-cobalt-700 dark:text-cobalt-300" data-id="element-80">
        <ShieldCheck className="w-4 h-4 mr-1.5" data-id="element-81" />
        All Loans In Process
      </div>;
  }
  const canSeeTeam = role === 'Team Lead';
  return <div className="flex items-center space-x-1 bg-slate-100 dark:bg-slate-800/80 p-1 rounded-lg w-fit border border-slate-200 dark:border-slate-700/60" data-id="element-82">
      <button onClick={() => setView('My Pipeline')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${view === 'My Pipeline' ? 'bg-white dark:bg-slate-700 text-cobalt-600 dark:text-cobalt-400 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`} data-id="element-83">
        My Pipeline
      </button>
      {canSeeTeam && <button onClick={() => setView('Team Pipeline')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${view === 'Team Pipeline' ? 'bg-white dark:bg-slate-700 text-cobalt-600 dark:text-cobalt-400 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`} data-id="element-84">
          Team Pipeline
        </button>}
    </div>;
}
interface AlertCardsProps {
  onOpenModal: (modal: ModalType) => void;
  counts: {
    newApp: number;
    sos: number;
    rateLock: number;
  };
}
export function AlertCards({
  onOpenModal,
  counts
}: AlertCardsProps) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-85">
      {/* New App — cobalt treatment */}
      <button onClick={() => onOpenModal('newApp')} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-xl border border-cobalt-200 dark:border-cobalt-900/50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all group text-left relative overflow-hidden" data-id="element-86">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-cobalt-500" data-id="element-87"></div>
        <div className="pl-2" data-id="element-88">
          <div className="flex items-center space-x-2 text-cobalt-600 dark:text-cobalt-400 mb-1" data-id="element-89">
            <FileText className="w-4 h-4" data-id="element-90" />
            <span className="text-sm font-medium" data-id="element-91">New App Alert</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white group-hover:text-cobalt-600 dark:group-hover:text-cobalt-400 transition-colors" data-id="element-92">
            {counts.newApp}
          </div>
        </div>
        <div className="w-10 h-10 rounded-full bg-cobalt-50 dark:bg-cobalt-900/20 flex items-center justify-center group-hover:bg-cobalt-100 dark:group-hover:bg-cobalt-900/40 transition-colors" data-id="element-93">
          <ChevronDown className="w-5 h-5 text-cobalt-500 -rotate-90" data-id="element-94" />
        </div>
      </button>

      {/* SOS — red treatment */}
      <button onClick={() => onOpenModal('sos')} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-xl border border-sos-200 dark:border-sos-900/50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all group text-left relative overflow-hidden" data-id="element-95">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-sos-500" data-id="element-96"></div>
        <div className="pl-2" data-id="element-97">
          <div className="flex items-center space-x-2 text-sos-600 dark:text-sos-400 mb-1" data-id="element-98">
            <AlertCircle className="w-4 h-4" data-id="element-99" />
            <span className="text-sm font-medium" data-id="element-100">SOS Alert</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white group-hover:text-sos-600 dark:group-hover:text-sos-400 transition-colors" data-id="element-101">
            {counts.sos}
          </div>
        </div>
        <div className="w-10 h-10 rounded-full bg-sos-50 dark:bg-sos-900/20 flex items-center justify-center group-hover:bg-sos-100 dark:group-hover:bg-sos-900/40 transition-colors" data-id="element-102">
          <ChevronDown className="w-5 h-5 text-sos-500 -rotate-90" data-id="element-103" />
        </div>
      </button>

      {/* Locks Expiring Soon — amber treatment */}
      <button onClick={() => onOpenModal('rateLock')} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-amber-900/40 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all group text-left relative overflow-hidden" data-id="element-104">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" data-id="element-105"></div>
        <div className="pl-2" data-id="element-106">
          <div className="flex items-center space-x-2 text-amber-600 dark:text-amber-500 mb-1" data-id="element-107">
            <Clock className="w-4 h-4" data-id="element-108" />
            <span className="text-sm font-medium" data-id="element-109">Locks Expiring Soon</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-500 transition-colors" data-id="element-110">
            {counts.rateLock}
          </div>
        </div>
        <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center group-hover:bg-amber-100 dark:group-hover:bg-amber-900/40 transition-colors" data-id="element-111">
          <ChevronDown className="w-5 h-5 text-amber-500 -rotate-90" data-id="element-112" />
        </div>
      </button>
    </div>;
}
export type FilterKey = 'processor' | 'processorAssistant' | 'loanOwner' | 'monthPushing' | 'lockExpiration' | 'scheduledClosing' | 'scheduledFunding';
export type FilterState = Record<FilterKey, string[]>;
interface FiltersBarProps {
  filters: FilterState;
  options: Record<FilterKey, string[]>;
  onChange: (key: FilterKey, value: string[]) => void;
  onClearAll: () => void;
}
const FILTER_DEFS: {
  key: FilterKey;
  label: string;
}[] = [{
  key: 'processor',
  label: 'Processor'
}, {
  key: 'processorAssistant',
  label: 'Processor Assistant'
}, {
  key: 'loanOwner',
  label: 'Loan Owner'
}, {
  key: 'monthPushing',
  label: 'Month Pushing'
}, {
  key: 'lockExpiration',
  label: 'Lock Expiration Date'
}, {
  key: 'scheduledClosing',
  label: 'Scheduled Closing Date'
}, {
  key: 'scheduledFunding',
  label: 'Scheduled Funding Date'
}];
export function FiltersBar({
  filters,
  options,
  onChange,
  onClearAll
}: FiltersBarProps) {
  const activeCount = Object.values(filters).reduce((sum, v) => sum + v.length, 0);
  return <div className="flex items-center gap-2 flex-wrap py-1" data-id="element-113">
      <div className="flex items-center text-slate-500 dark:text-slate-400 mr-1 flex-shrink-0" data-id="element-114">
        <Filter className="w-4 h-4 mr-2" data-id="element-115" />
        <span className="text-sm font-medium" data-id="element-116">Filters</span>
        {activeCount > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-cobalt-100 dark:bg-cobalt-900/40 text-cobalt-700 dark:text-cobalt-300 text-[10px] font-semibold" data-id="element-117">
            {activeCount}
          </span>}
        <span className="text-slate-300 dark:text-slate-600 mx-2" data-id="element-118">·</span>
      </div>

      {FILTER_DEFS.map(def => <MultiSelect key={def.key} label={def.label} options={options[def.key] || []} selected={filters[def.key] || []} onChange={next => onChange(def.key, next)} data-id="element-119" />)}

      <button onClick={onClearAll} disabled={activeCount === 0} className="flex-shrink-0 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white px-2 flex items-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-500 dark:disabled:hover:text-slate-400 ml-1" data-id="element-120">
        <X className="w-3 h-3 mr-1" data-id="element-121" /> Clear all
      </button>
    </div>;
}